import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from 'src/app/_metronic/shared/components/toast/toast.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SharedModule } from 'src/app/_metronic/shared/shared.module';
import { CommonModalComponent } from 'src/app/_metronic/shared/components/common-modal/common-modal.component';

@Component({
  selector: 'app-set-password-form',
  standalone: true,
  imports: [CommonModule, SharedModule, ReactiveFormsModule, TranslateModule], 
  templateUrl: './set-password-form.component.html',
  styleUrls: ['./set-password-form.component.scss']
})
export class SetPasswordFormComponent implements OnInit {
  @Input() mode: 'reset' | 'create' = 'reset';
  @Input() token: string = '';
  @Input() email: string = '';
  
  @Output() backClick = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();
  @Output() linkExpired = new EventEmitter<void>();

  form: FormGroup;
  isLoading = false;
  isVerifyingToken = false;
  showPassword = false;
  showConfirmPassword = false;
  showSuccessModal = false;

  passwordValidations = {
    length: false,
    upper: false,
    lower: false,
    number: false,
    special: false
  };

  @ViewChild('successModal') successModal: CommonModalComponent;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toastService: ToastService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      newPassword: ['', [Validators.required, this.passwordStrengthValidator.bind(this)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }
  
  ngOnInit(): void {
    // Monitor password changes for validation indicators
    this.form.get('newPassword')?.valueChanges.subscribe(value => {
      this.updatePasswordValidations(value);
    });
    
    this.checkLinkValidity();
  }

  checkLinkValidity(): void {
    // According to user request: {{api_url}}/auth/check-valid-link?type=ACTIVATION_USER&token=...
    // The type should be adapted based on this.mode (reset -> FORGOT_PASSWORD, create -> ACTIVATION_USER)
    // However, user specifically mentioned "type nya ada ACTIVATION_USER dan FORGOT_PASSWORD sesuaikan dengan type login saat ini."
    // Assuming 'create' map to 'ACTIVATION_USER' and 'reset' map to 'FORGOT_PASSWORD'
    
    // Actually, user provided example: login?type=create-password & type=ACTIVATION_USER
    // Let's map strict types.
    
    let apiType = 'FORGOT_PASSWORD';
    if (this.mode === 'create') {
        apiType = 'ACTIVATION_USER';
    }

    this.isVerifyingToken = true;
    this.authService.checkValidLink(apiType, this.token).subscribe({
        next: (res) => {
            this.isVerifyingToken = false;
            const isValid = res?.response_output?.detail?.is_valid;
            if (!isValid) {
                this.linkExpired.emit();
            }
            this.cdr.detectChanges();
        },
        error: (err) => {
            this.isVerifyingToken = false;
            this.linkExpired.emit();
            this.cdr.detectChanges();
        }
    });
  }

  get f() {
    return this.form.controls;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  passwordStrengthValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value || '';
    const hasUpper = /[A-Z]/.test(value);
    const hasLower = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[,!@]/.test(value); // Adjust special chars as needed based on requirement
    const isValidLength = value.length >= 8;

    const valid = hasUpper && hasLower && hasNumber && hasSpecial && isValidLength;

    return valid ? null : { passwordWeak: true };
  }

  updatePasswordValidations(value: string): void {
    this.passwordValidations = {
      length: value.length >= 8,
      upper: /[A-Z]/.test(value),
      lower: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      special: /[,!@]/.test(value) // Matches requirement: , ! @
    };
  }

  passwordMatchValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.get('newPassword');
    const confirm = control.get('confirmPassword');

    if (!password || !confirm) return null;

    if (password.value !== confirm.value) {
      confirm.setErrors({ ...confirm.errors, passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      if (confirm.errors && confirm.errors['passwordMismatch']) {
        const { passwordMismatch, ...otherErrors } = confirm.errors;
        confirm.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
      }
      return null;
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;
    const { newPassword, confirmPassword } = this.form.value;

    let apiType = 'FORGOT_PASSWORD';
    if (this.mode === 'create') {
        apiType = 'ACTIVATION_USER';
    }

    const payload = {
        token: this.token,
        type: apiType,
        password: newPassword,
        confirm_password: confirmPassword
    };

    this.authService.setPassword(payload).subscribe({
        next: (res) => {
            this.isLoading = false;
            this.successModal.open();
        },
        error: (err) => {
            this.isLoading = false;
            const errorMsg = err?.error?.message || 'An error occurred';
            this.toastService.error(errorMsg);
        }
    });
  }

  onModalClose(): void {
    this.success.emit();
  }
  
  onModalOk(): void {
    this.success.emit();
  }

  onBackClick(): void {
    this.backClick.emit();
  }
}
